/*
 * Photo Headshot — state machine.
 *
 * Tracks the live PowerPoint selection (mirrors useActiveSlideNotes.ts's
 * DocumentSelectionChanged wiring) so the panel can show the design gallery
 * only once a single portrait image is selected. Running a design walks:
 * read selection -> capture+upload the photo -> detect the face (DeckorateAPI)
 * -> crop to it -> compose the design onto the slide.
 */

import { useCallback } from "react";
import { useDeckorateHttp } from "../../../hooks/useDeckorateHttp";
import { useSelectionWatcher } from "../../shared/hooks/useSelectionWatcher";
import { runAbortable, useAbortableRun } from "../../shared/hooks/useAbortableRun";
import {
  describePhotoHeadFailure,
  getErrorMessage,
  logPhotoHeadError,
  postPhotoHeadDetect,
  PHOTO_HEAD_REQUEST_TIMEOUT_MS,
} from "../services/photoHeadApi";
import { cropToFace } from "../services/photoHeadCrop";
import { insertPhotoHead } from "../services/photoHeadInsert";
import {
  captureSelectedPhoto,
  NOT_AN_IMAGE_MESSAGE,
  readSelectedPhotoShape,
} from "../services/photoHeadSelection";
import { uploadPhotoHeadImage } from "../services/photoHeadUpload";
import type { PhotoHeadDesign, PhotoHeadshotState } from "../../../types";

const PHOTO_HEAD_FAILED_MESSAGE = "Creating the photo headshot failed";
const NO_FACE_MESSAGE = "No face was detected in this photo — try a different image.";

// NOTE: hardcoded to the only Studio client that exists today. Once
// photoHeadUpload.ts's blob-sas call needs to support other tenants, replace
// this with the real client id (e.g. useOptionalAppConfig()?.data?.client.id,
// the same field TaskPane.tsx reads) and drop this constant.
const PHOTO_HEAD_CLIENT_ID = 1;

function newRequestId(): string {
  return globalThis.crypto?.randomUUID?.() ?? `photo-head-${Date.now()}`;
}

const SELECTION_WATCHER_REASONS = {
  handlerAdd: "photo-head-selection-handler-add",
  handlerRemove: "photo-head-selection-handler-remove",
};

export function usePhotoHeadshot() {
  const http = useDeckorateHttp();
  const { shape: selectedShape, selectionError } = useSelectionWatcher(
    readSelectedPhotoShape,
    NOT_AN_IMAGE_MESSAGE,
    logPhotoHeadError,
    SELECTION_WATCHER_REASONS,
  );
  const { state, safeSetState, isRunningRef, abortRef, timedOutRef, cancel, reset } = useAbortableRun<PhotoHeadshotState>({
    status: "idle",
  });

  const run = useCallback(
    async (design: PhotoHeadDesign) => {
      if (isRunningRef.current) return;
      if (!selectedShape) {
        safeSetState({ status: "error", message: NOT_AN_IMAGE_MESSAGE });
        return;
      }

      const shape = selectedShape;
      safeSetState({ status: "running", phase: "reading", designId: design.id });

      const outcome = await runAbortable({ isRunningRef, abortRef, timedOutRef }, PHOTO_HEAD_REQUEST_TIMEOUT_MS, async (signal) => {
        const source = await captureSelectedPhoto(shape);

        safeSetState({ status: "running", phase: "detecting", designId: design.id });
        const uploaded = await uploadPhotoHeadImage(http, PHOTO_HEAD_CLIENT_ID, source.base64, source.mimeType, { signal });
        const detection = await postPhotoHeadDetect(
          http,
          { requestId: newRequestId(), blobContainer: uploaded.blobContainer, blobName: uploaded.blobName },
          { signal },
        );
        if (!detection.found || !detection.faceBox) {
          return { kind: "no-face" as const, message: detection.message ?? NO_FACE_MESSAGE };
        }

        safeSetState({ status: "running", phase: "cropping", designId: design.id });
        const cropped = await cropToFace(source.base64, source.mimeType, detection.faceBox);

        safeSetState({ status: "running", phase: "composing", designId: design.id });
        await insertPhotoHead(shape, design, cropped.base64);

        return { kind: "done" as const };
      });

      switch (outcome.kind) {
        case "skipped":
          return;
        case "cancelled":
          safeSetState({ status: "cancelled" });
          return;
        case "failed":
          logPhotoHeadError("photo-head-run", getErrorMessage(outcome.error, PHOTO_HEAD_FAILED_MESSAGE));
          safeSetState({
            status: "error",
            message: describePhotoHeadFailure(outcome.error, PHOTO_HEAD_FAILED_MESSAGE, { timedOut: outcome.timedOut }),
          });
          return;
        case "ok":
          if (outcome.value.kind === "no-face") {
            safeSetState({ status: "error", message: outcome.value.message });
            return;
          }
          safeSetState({ status: "done", designId: design.id });
      }
    },
    [http, selectedShape, safeSetState, isRunningRef, abortRef, timedOutRef],
  );

  return { state, selectedShape, selectionError, run, cancel, reset };
}
